import { useRef, useCallback, useEffect, useState } from "react";
import { Logger } from "@cede/utils";
import { THROTTLE_DELAY, MAX_RECONNECT_ATTEMPTS, MAX_RECONNECT_DELAY } from "../constants";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";

const EXCHANGE_ID = "binance";

/**
 * Format pair symbol for Binance WebSocket
 * BTC/USDT → btcusdt
 */
function formatSymbol(pairSymbol: string): string {
  return pairSymbol.replace("/", "").toLowerCase();
}

/**
 * Build WebSocket URL for Binance orderbook
 * Uses depth20@100ms stream for 20 levels updated every 100ms
 */
function buildWebSocketUrl(symbol: string): string {
  return `wss://stream.binance.com:9443/ws/${symbol}@depth20@100ms`;
}

/**
 * Hook for connecting to Binance WebSocket and subscribing to orderbook updates
 * Binance uses a direct URL pattern - no subscription message needed
 */
export const useBinanceOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const isConnecting = useRef(false);
  const reconnectAttemptsRef = useRef<number>(0);
  const connectWsRef = useRef<(() => void) | null>(null);
  const disconnectWsRef = useRef<(() => void) | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateOrderBookData = useCallback((data: DirectOrderBookSnapshot) => {
    setOrderBookData(data);
    setError(null);
  }, []);

  const handleOrderBookUpdate = useCallback(
    (data: DirectOrderBookSnapshot) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        // Enough time has passed, update immediately
        lastUpdateTimeRef.current = now;
        updateOrderBookData(data);
      } else {
        // Throttle: schedule update for later
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }

        const remainingTime = THROTTLE_DELAY - timeSinceLastUpdate;
        throttleTimeoutRef.current = setTimeout(() => {
          lastUpdateTimeRef.current = Date.now();
          updateOrderBookData(data);
        }, remainingTime);
      }
    },
    [updateOrderBookData],
  );

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Clear any pending reconnect attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    // Clear throttle timeout
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    // Reset connection state
    isConnecting.current = false;
    setIsConnected(false);
    lastUpdateTimeRef.current = 0;
  }, []);

  const connectWs = useCallback(() => {
    // Only connect if not already connecting, not already connected, enabled, and have valid pairSymbol
    if (isConnecting.current || wsRef.current || !enabled || !pairSymbol) return;

    // Clear any pending reconnect attempts before connecting
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    isConnecting.current = true;
    setError(null);
    setOrderBookData(null);

    try {
      const symbol = formatSymbol(pairSymbol);
      const url = buildWebSocketUrl(symbol);
      const ws = new WebSocket(url);

      ws.onopen = () => {
        setIsConnected(true);
        isConnecting.current = false;
        reconnectAttemptsRef.current = 0; // Reset attempts on successful connection

        // Clear any pending reconnect attempts
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Binance depth stream format:
          // { lastUpdateId, bids: [[price, qty]], asks: [[price, qty]] }
          if (message.bids && message.asks) {
            const bids: [number, number][] = message.bids.map((level: [string, string]) => [
              parseFloat(level[0]),
              parseFloat(level[1]),
            ]);

            const asks: [number, number][] = message.asks.map((level: [string, string]) => [
              parseFloat(level[0]),
              parseFloat(level[1]),
            ]);

            const snapshot: DirectOrderBookSnapshot = {
              timestamp: Date.now(),
              exchangeId: EXCHANGE_ID,
              pairSymbol,
              bids,
              asks,
            };

            handleOrderBookUpdate(snapshot);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to parse orderbook update:`, errorMessage, err);
          setError("Failed to parse orderbook update");
        }
      };

      ws.onerror = () => {
        isConnecting.current = false;
        setIsConnected(false);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        isConnecting.current = false;
        setIsConnected(false);

        // Attempt to reconnect with exponential backoff if still enabled
        if (enabled && pairSymbol && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s, up to 30s max
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY);
          reconnectAttemptsRef.current++;

          Logger.log(
            `[${EXCHANGE_ID}OrderBookWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
            Logger.types.INFO,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWsRef.current?.();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Max reconnection attempts reached, giving up`);
          setError("Failed to reconnect after multiple attempts");
        }
      };

      wsRef.current = ws;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to create WebSocket connection:`, errorMessage, err);
      isConnecting.current = false;
      setError("Failed to create WebSocket connection");
    }
  }, [pairSymbol, enabled, handleOrderBookUpdate]);

  // Keep refs updated with latest function references
  connectWsRef.current = connectWs;
  disconnectWsRef.current = disconnectWs;

  // Handle connection and visibility changes
  useEffect(() => {
    if (!enabled || !pairSymbol) {
      disconnectWsRef.current?.();
      return;
    }

    // Connect initially
    connectWsRef.current?.();

    // Handle visibility changes
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectWsRef.current?.();
      } else if (enabled && pairSymbol) {
        connectWsRef.current?.();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
      disconnectWsRef.current?.();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [pairSymbol, enabled]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error,
  };
};
